import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { t } from '@lingui/macro'
import { Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import LoadingButton from '@mui/lab/LoadingButton'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

import { StaffData } from './StaffSelectionTable'

import WizardBasicsStep from './wizard/WizardBasicsStep'
import WizardSpecialitiesStep from './wizard/WizardSpecialitiesStep'
import WizardStaffStep from './wizard/WizardStaffStep'
import WizardOptionsStep from './wizard/WizardOptionsStep'
import WizardReviewStep from './wizard/WizardReviewStep'

export type CreateMeetingLinkWizardValues = {
  busyEnabled?: boolean
  description: string
  name: string
  specialities: { code: string; name: string; price: number }[]
  staffs: string[]
  immediateConsultationConfig: {
    enabled: boolean
    maxWaitingPatients: number | null
    requirePaymentUpfront: boolean
    price: number | null
    paymentReceiver: string | null
  }
  waitingRoom: string | null
}

export type CreateMeetingLinkWizardProps = {
  disableStaffSelection?: boolean
  onSubmit: (values: CreateMeetingLinkWizardValues) => void
  initialValues: CreateMeetingLinkWizardValues
  isSubmitting?: boolean
  staffs: StaffData[]
  SpecialitiesSelection: React.ReactElement
}

const validationSchemas = [
  // Step 0: Basics
  yup.object().shape({
    name: yup.string().required(),
    description: yup.string(),
    busyEnabled: yup.boolean(),
  }),
  // Step 1: Specialities
  yup.object().shape({
    specialities: yup.array().of(
      yup.object({ price: yup.number().required() })
    ),
  }),
  // Step 2: Staff
  yup.object().shape({
    staffs: yup.array().of(yup.string()),
  }),
  // Step 3: Options
  yup.object().shape({
    immediateConsultationConfig: yup.object({
      enabled: yup.boolean().required(),
      maxWaitingPatients: yup.number().nullable(),
      requirePaymentUpfront: yup.boolean().required(),
      price: yup.number().nullable().when(['enabled', 'requirePaymentUpfront'], {
        is: (enabled: boolean, requirePayment: boolean) => enabled && requirePayment,
        then: (schema) => schema.min(0, t`Price must be positive`),
      }),
      paymentReceiver: yup.string().nullable().when(['enabled', 'requirePaymentUpfront'], {
        is: (enabled: boolean, requirePayment: boolean) => enabled && requirePayment,
        then: (schema) => schema.required(t`Payment receiver is required when payment is enabled`),
      }),
    }),
    waitingRoom: yup.string().nullable().when('immediateConsultationConfig.enabled', {
      is: true,
      then: (schema) => schema.required(t`Waiting room is required`),
    }),
  }),
  // Step 4: Review (no validation)
  yup.object(),
]

export function CreateMeetingLinkWizard(props: CreateMeetingLinkWizardProps) {
  const { disableStaffSelection = false, initialValues, isSubmitting, onSubmit, staffs, SpecialitiesSelection } = props
  const { t } = useTranslation()
  const formikRef = useRef<FormikProps<CreateMeetingLinkWizardValues>>(null)

  const steps = [
    t('page.createMeetingLink.wizard.step.basics'),
    t('page.createMeetingLink.wizard.step.specialities'),
    ...(disableStaffSelection ? [] : [t('page.createMeetingLink.wizard.step.staff')]),
    t('page.createMeetingLink.wizard.step.options'),
    t('page.createMeetingLink.wizard.step.review'),
  ]

  // Map UI step index to logical step content
  const getStepContent = (uiStep: number) => {
    let logicalStep = uiStep
    // If staff selection is disabled, skip step 2 in logical mapping
    if (disableStaffSelection && uiStep >= 2) {
      logicalStep = uiStep + 1
    }

    switch (logicalStep) {
      case 0:
        return <WizardBasicsStep />
      case 1:
        return <WizardSpecialitiesStep SpecialitiesSelection={SpecialitiesSelection} />
      case 2:
        return <WizardStaffStep staffs={staffs} />
      case 3:
        return <WizardOptionsStep staffs={staffs} />
      case 4:
        return <WizardReviewStep staffs={staffs} />
      default:
        return null
    }
  }

  const getValidationSchema = (uiStep: number) => {
    let logicalStep = uiStep
    if (disableStaffSelection && uiStep >= 2) {
      logicalStep = uiStep + 1
    }
    return validationSchemas[logicalStep]
  }

  const [activeStep, setActiveStep] = useState(0)
  const isLastStep = activeStep === steps.length - 1

  const handleNext = async () => {
    if (!formikRef.current) return
    const errors = await formikRef.current.validateForm()
    const touched: Record<string, boolean> = {}

    // Touch all fields so errors show
    Object.keys(formikRef.current.values).forEach((key) => {
      touched[key] = true
    })
    formikRef.current.setTouched(touched)

    if (Object.keys(errors).length === 0) {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleSubmit = (values: CreateMeetingLinkWizardValues) => {
    if (isLastStep) {
      onSubmit(values)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      innerRef={formikRef}
      onSubmit={handleSubmit}
      validationSchema={getValidationSchema(activeStep)}
      validateOnMount={false}
    >
      {({ handleSubmit: formikHandleSubmit }) => (
        <Form onSubmit={formikHandleSubmit}>
          <Container maxWidth="md">
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <Box sx={{ minHeight: 300, py: 2 }}>
              {getStepContent(activeStep)}
            </Box>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                data-testid="meeting-link-wizard-back"
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                variant="outlined"
              >
                {t('page.createMeetingLink.wizard.back')}
              </Button>

              {isLastStep ? (
                <LoadingButton
                  data-testid="meeting-link-wizard-submit"
                  color="primary"
                  disableElevation
                  loading={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  {t('page.createMeetingLink.wizard.create')}
                </LoadingButton>
              ) : (
                <Button
                  data-testid="meeting-link-wizard-next"
                  onClick={handleNext}
                  endIcon={<ArrowForwardIcon />}
                  variant="contained"
                  disableElevation
                >
                  {t('page.createMeetingLink.wizard.next')}
                </Button>
              )}
            </Stack>
          </Container>
        </Form>
      )}
    </Formik>
  )
}

export default CreateMeetingLinkWizard
